import { Component, ViewChild, ElementRef, OnDestroy, Input, EventEmitter, Renderer2, Output, PLATFORM_ID, Inject, AfterViewInit } from '@angular/core';
declare const require: any;
let THREE;
declare let marmoset: any;
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { isPlatformBrowser } from '@angular/common';
import { timer } from 'rxjs';
import { ThreeDFile } from '../ThreeDFile';
import { loadLazyJsFile } from 'src/app/util';


@Component({
  selector: 'three-view',
  templateUrl: './three-view.component.html',
  styleUrls: ['./three-view.component.scss']
})
export class ThreeViewComponent implements OnDestroy, AfterViewInit {
  /** datetimepicker 插件初始化完成後觸發 */
  static pulginInit = new EventEmitter<void>();
  /** 全域的 bootstrap-table 插件初始化完成 */
  static isPluginInit = false;
  /** 全域的 datetimepicker 插件在正初始化中 */
  static isPluginIniting = false;
  /** 可以開始使用元件了 */
  canInit = false;
  /** 指令初始化完成 */
  @Output() init = new EventEmitter<void>();
  /** 是否渲染端為瀏覽器 */
  protected isBrowser: boolean;
  @Input() fileTemp: ThreeDFile;
  myviewer: any;
  @ViewChild('loadingBar', {static: false}) private loadingBar: ElementRef;
  @ViewChild('canvas', {static: true})
  private canvasRef: ElementRef;
  private get canvas(): HTMLCanvasElement {
      return this.canvasRef.nativeElement;
  }
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  controls: OrbitControls;
  windowRequestId: any;
  hiddenCavans = true;
  marmoset3D = false;
  /** loadingBar 的寬度 */
  loadingBarWidth = 0;
  /** loadingBar 的高度 */
  loadingBarHeight = 0;
  loadValue = 0;
  /** 載入插件的方法 */
  static loadPlugin(renderer: Renderer2, parentEl: HTMLElement, isBrowser: boolean) {
    ThreeViewComponent.isPluginIniting = true;
    loadLazyJsFile(renderer, 'marmosetJs', '/assets/marmoset/marmoset.js');
    checkPluginCanUse(() => {
      ThreeViewComponent.isPluginInit = true;
      ThreeViewComponent.pulginInit.emit();
      ThreeViewComponent.pulginInit.complete();
    });
  }

  constructor(
    @Inject(PLATFORM_ID) protected platformId: any,
    protected el: ElementRef,
    protected render: Renderer2,
  ) {
    /** 因應 ssr 所以搬到 constructor */
    THREE = require('three');
    window['THREE'] = THREE;
    require('three/examples/js/loaders/DRACOLoader');
    THREE.DRACOLoader.setDecoderPath( 'three/examples/js/lib/draco/' );
    THREE.DRACOLoader.setDecoderConfig( { type: 'js' } ); // (Optional) Override detection of WASM support.


    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      if (!ThreeViewComponent.isPluginInit && !ThreeViewComponent.isPluginIniting) {
        if (isPluginLoaded()) {
          ThreeViewComponent.isPluginInit = true;
        } else {
          ThreeViewComponent.loadPlugin(this.render, this.el.nativeElement, this.isBrowser);
        }
      }
    }
  }

  ngAfterViewInit() {
    this.loadingBarWidth = this.loadingBar.nativeElement.clientWidth;
    this.loadingBarHeight = this.loadingBar.nativeElement.clientHeight;

    if (this.isBrowser) {
      if (!ThreeViewComponent.isPluginInit) {
        ThreeViewComponent.pulginInit.subscribe(() => {
          this._onPluginInit();
        });
      } else {
        this._onPluginInit();
      }
    }
  }

  protected _onPluginInit() {
    if (this.fileTemp.ext === 'mview') {
      const myviewerMapDom = document.getElementById('myviewerMap');

      this.myviewer = new marmoset.WebViewer( 800, 600, this.fileTemp.original );
      myviewerMapDom.appendChild( this.myviewer.domRoot );
      this.myviewer.loadScene();
      this.myviewer.onLoad = () => {
        this.marmoset3D = true;
        this.myviewer.resize(myviewerMapDom.scrollWidth, myviewerMapDom.scrollHeight);
      };
    } else {
      this.initThreeView();
      this.animate();
    }
  }

  ngOnDestroy() {
    if (this.myviewer) {
      this.myviewer.unload();
    }
    if (this.windowRequestId) {
      window.cancelAnimationFrame(this.windowRequestId);
      this.windowRequestId = undefined;
    }
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
  }

  initThreeView() {
    this.scene = new THREE.Scene();
    const texture = new THREE.TextureLoader().load('/assets/3d/bg-3d.png');
    this.scene.background = texture;

    if (this.fileTemp.ext === 'glb' || this.fileTemp.ext === 'gltf' || this.fileTemp.ext === 'zip' || this.fileTemp.ext === 'rar' || this.fileTemp.ext === '7z') {
      this.loadGLTF();
    } else if (this.fileTemp.ext === 'obj') {
      this.loadOBJ();
    }

    this.camera = new THREE.PerspectiveCamera(
      45,
      this.loadingBarWidth / this.loadingBarHeight, // 相機的外觀比例
      0.01,
      1000
    );

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.physicallyCorrectLights = true; // 是否使用物理上正確的光照模式
    this.renderer.gammaOutput = true; // 如果設定, 那麼它期望所有紋理和顏色需要乘以gamma輸出。 預設值false
    this.renderer.gammaFactor = 2.2;
    this.renderer.setClearColor( 0xcccccc ); // 設定顏色及其透明度
    this.renderer.setPixelRatio( window.devicePixelRatio ); // 設定裝置畫素比。通常用於避免HiDPI裝置上繪圖模糊


    this.camera.position.set(0, 0, 50);
    this.camera.lookAt(new THREE.Vector3(0, 0, 0));

    const directionalLight = new THREE.DirectionalLight(0xFFFFFF, 0.8 * Math.PI); // 平行光
    directionalLight.position.set(0.5, 0, 0.866);
    const ambientLight = new THREE.AmbientLight(0xFFFFFF, 1); // 環境光 soft white light

    this.renderer.setSize(this.loadingBarWidth , this.loadingBarHeight);

    this.scene.add(this.camera);

    // this.scene.add(directionalLight, ambientLight);
    // this.camera.add(ambientLight);
    this.camera.add(directionalLight, ambientLight);

    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.enableZoom = true;

    this.canInit = true;
    this.init.emit();
  }

  loadGLTF() {
    const loader = new GLTFLoader();
    const dracoLoader = new THREE.DRACOLoader();
    loader.setDRACOLoader( dracoLoader );
    loader.load(
      this.fileTemp.ext === 'glb' || this.fileTemp.ext === 'gltf' ? this.fileTemp.original : this.fileTemp.transform.gltf,
      gltf => {
        const box = new THREE.Box3().setFromObject( gltf.scene );
        const size = box.getSize( new THREE.Vector3() ).length();
        const center = box.getCenter( new THREE.Vector3() );

        gltf.scene.position.x += ( gltf.scene.position.x - center.x );
        gltf.scene.position.y += ( gltf.scene.position.y - center.y );
        gltf.scene.position.z += ( gltf.scene.position.z - center.z );

        this.scene.add(gltf.scene);
        this.hiddenCavans = false;
      },
      xhr => {
        this.loadValue = Math.round( xhr.loaded / xhr.total * 100 );
      },
      error => console.error( error )
    );
  }

  loadOBJ() {
    const loader = new OBJLoader();
    loader.load(
      this.fileTemp.original,
      (obj) => {
        this.scene.add( obj );
        this.hiddenCavans = false;
      },
      xhr => {
        this.loadValue = Math.round( xhr.loaded / xhr.total * 100 );
      },
      error => console.error( error )
    );
  }

  animate() {
    this.windowRequestId = window.requestAnimationFrame(() => this.animate());
    this.renderer.render(this.scene, this.camera);
  }
}

function isPluginLoaded() {
  return 'undefined' !== typeof window['marmoset'];
}

const checkPluginCanUse = (cb: () => void) => {
  const cpTmSubc = timer(0, 300).subscribe(t => {
    if (isPluginLoaded()) {
      cpTmSubc.unsubscribe();
      cb();
    }
  });
}
